import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import ProductActions, { isPdpFulfillmentEligibleSku } from 'actions/ProductActions';
import userActions from 'actions/UserActions';
import profileApi from 'services/api/profile';
import ExtraProductDetailsUtils from 'utils/ExtraProductDetailsUtils';
import userUtils from 'utils/User';
import basketUtils from 'utils/Basket';
import storeUtils from 'utils/Store';
import cookieUtils from 'utils/Cookies';
import userLocationUtils from 'utils/UserLocation';
import anaConsts from 'analytics/constants';
import * as pdpAnalytics from 'analytics/productPage/pdpAnalytics';
import Empty from 'constants/empty';
import store from 'store/Store';

const { getFulfillmentOptions } = ProductActions;

const useFulfillment = () => {
    const dispatch = useDispatch();
    const currentProduct = useSelector(state => state.page.product, shallowEqual);
    const preferredStoreInfo = useSelector(state => state.user.preferredStoreInfo, shallowEqual);
    const preferredZipCode = useSelector(state => state.user.preferredZipCode);
    const userProfileId = useSelector(state => state.user.profileId);
    const testTarget = useSelector(state => state.testTarget);

    const isBopisFirstOption = !!testTarget?.offers?.reorderFulfillmentOptionsPdp?.show;

    const [selectedMethod, setSelectedMethod] = useState('shipped');
    const [productExtraDetailsLoaded, setProductExtraDetailsLoaded] = useState(true);
    const [sameDayAvailable, setSameDayAvailable] = useState(true);
    const [serviceUnavailable, setServiceUnavailable] = useState(false);
    const [sameDayNotAvailableForZip, setSameDayNotAvailableForZip] = useState(false);
    const [isSDUAddedToBasket, setIsSDUAddedToBasket] = useState(false);
    const [skuTrialEligibility, setSkuTrialEligibility] = useState(false);
    const [skuTrialPeriod, setSkuTrialPeriod] = useState('');
    const [isUserSDUTrialEligible, setIsUserSDUTrialEligible] = useState(false);
    const [isSDUBannerVisible, setIsSDUBannerVisible] = useState(false);
    const [trackedStoreId, setTrackedStoreId] = useState('');
    const [isKohlsStore, setIsKohlsStore] = useState(false);

    const stateRef = useRef({});
    const selectPickupRef = useRef();
    const prevProductIdRef = useRef(currentProduct?.productDetails?.productId);
    // GUAR-8371: initialize with the current zip so the initial-mount effect
    // does NOT re-fire getFulfillmentOptions — ProductActions.openPage
    // (SPA nav) and ProductPage.componentDidMount (hard load) now dispatch it
    // earlier. Runtime zip changes (user edits header flyout) still fire
    // because the ref lags behind the next preferredZipCode value.
    const prevZipCodeRef = useRef(preferredZipCode);

    useEffect(() => {
        stateRef.current = {
            selectedMethod,
            productExtraDetailsLoaded,
            isSDUBannerVisible,
            isBopisFirstOption
        };
    });

    const getCurrentAvailability = productData => {
        const currentSku = currentProduct?.currentSku;
        const noExtraProd = currentProduct?.noExtraPropertiesProduct;
        const extraSku = noExtraProd?.currentSku;

        const stockAvailability =
            productData?.currentSku?.actionFlags?.availabilityStatus ||
            productData?.currentSku?.actionFlags?.sameDayAvailabilityStatus ||
            ExtraProductDetailsUtils.getStockAvailabilityStatus(extraSku) ||
            currentSku?.actionFlags?.availabilityStatus;

        return {
            isInStock: ExtraProductDetailsUtils.isinStock(stockAvailability),
            stockAvailability
        };
    };

    const fireShippingAnalytics = (value, productData, sduBannerOverride) => {
        const currentSku = currentProduct?.currentSku;
        const { isInStock, stockAvailability } = getCurrentAvailability(productData);
        const sduVis = sduBannerOverride !== undefined ? sduBannerOverride : stateRef.current.isSDUBannerVisible;

        pdpAnalytics.trackFulfillmentMethodChange(value, currentSku, {
            preferredStoreId: preferredStoreInfo?.storeId,
            customerZipCode: userUtils.getZipCode(),
            stockAvailability,
            isInStock,
            isSDUBannerVisible: sduVis,
            productData
        });
    };

    const selectShipped = () => {
        if (selectedMethod === 'shipped') {
            return;
        }

        if (currentProduct?.noExtraPropertiesProduct) {
            dispatch(ProductActions.restoreProductWithNoExtraDetails());
        }

        setSelectedMethod('shipped');
        setProductExtraDetailsLoaded(true);
    };

    const resetGetItShipped = callback => {
        if (callback) {
            callback();
        }
    };

    const handlePickupResponse = (data, storeInfo) => {
        if (data.currentSku.actionFlags?.isBopisableStore && data.currentSku.actionFlags?.isBopisableStore !== storeInfo.isBopisable) {
            const updated = { ...storeInfo, isBopisable: true };
            storeUtils.cacheStoreData(updated);
            dispatch(userActions.updatePreferredStore({ preferredStoreInfo: updated }));
        }

        dispatch(ProductActions.updateReserveOnlinePickUpInStoreProductDetails(data));
        setSelectedMethod('pickup');
        setProductExtraDetailsLoaded(true);
        fireShippingAnalytics(anaConsts.DELIVERY_OPTIONS_MAP.Pickup, data);
    };

    const disablePickup = () => {
        setSelectedMethod('pickup');
        setProductExtraDetailsLoaded(false);
    };

    const fetchPickupDetails = (storeId, skuId, shouldSwitch, storeInfo) => {
        const switchFn = shouldSwitch ? profileApi.switchPreferredStore : () => Promise.resolve();

        switchFn(storeId, true)
            .then(response => {
                if (response?.success) {
                    basketUtils.cachePickupStore(storeInfo, true);

                    if (Sephora.configurationSettings.setZipStoreCookie) {
                        cookieUtils.write(cookieUtils.KEYS.PREFERRED_STORE, storeInfo.storeId, null, false, false);
                    }
                }

                const productId = currentProduct?.productDetails?.productId;
                profileApi
                    .getRopisSpecificProductDetails(userProfileId, productId, skuId, 'pdp')
                    .then(data => handlePickupResponse(data, storeInfo))
                    .catch(disablePickup);
            })
            .catch(disablePickup);
    };

    const selectPickup = (event, ignoreState = false) => {
        if (!ignoreState && selectedMethod === 'pickup' && !isBopisFirstOption) {
            return;
        }

        setTrackedStoreId('');

        if (ExtraProductDetailsUtils.hasProductExtraProps(currentProduct)) {
            dispatch(ProductActions.restoreProductWithNoExtraDetails());
        }

        const storeId = preferredStoreInfo?.storeId;
        const cachedPickupStore = basketUtils.getCachedPickupStore();
        const shouldSwitch = userUtils.isAnonymous() && (!cachedPickupStore || storeId !== cachedPickupStore.storeId);

        if (!storeId && shouldSwitch) {
            disablePickup();

            return;
        }

        const skuId = currentProduct?.currentSku?.skuId;
        fetchPickupDetails(storeId, skuId, shouldSwitch, preferredStoreInfo);
    };

    selectPickupRef.current = selectPickup;

    const handleSameDayResponse = data => {
        const user = store.getState().user;
        const { userSubscriptions } = user || {};
        const sduSub = userSubscriptions?.filter(sub => sub.type === 'SDU') || Empty.Array;
        const trialEligible = userUtils.isAnonymous() ? true : sduSub.length > 0 && sduSub[0].isTrialEligible;
        const eligibleForSubscription = sduSub.length > 0 && !sduSub[0].isTrialEligible && sduSub[0].status === 'INACTIVE';
        const bannerVisible = trialEligible || data.SDUProduct?.isSDUAddedToBasket || eligibleForSubscription;

        setProductExtraDetailsLoaded(true);
        setIsSDUAddedToBasket(data.SDUProduct?.isSDUAddedToBasket);
        setSkuTrialEligibility(data.SDUProduct?.skuTrialEligibility);
        setSkuTrialPeriod(data.SDUProduct?.skuTrialPeriod);
        setIsUserSDUTrialEligible(trialEligible);
        setIsSDUBannerVisible(bannerVisible);
        fireShippingAnalytics(anaConsts.DELIVERY_OPTIONS_MAP.Sameday, data, bannerVisible);
    };

    const handleSameDayError = err => {
        const exception = err?.errorMessages;
        const serviceUnavailableErrors = err?.errors?.serviceUnavailable?.length > 0;
        const serviceExceptionErrors = err?.errors?.serviceException?.length > 0;
        const invalidInputErrors = err?.errors?.invalidInput?.length > 0;
        const zipcodeException = err?.errors?.ZipcodeException?.length > 0;

        if (exception) {
            setSameDayAvailable(false);
            setServiceUnavailable(serviceExceptionErrors || serviceUnavailableErrors);
            setSameDayNotAvailableForZip(zipcodeException || invalidInputErrors);
        }
    };

    const fetchSameDayDetails = () => {
        if (ExtraProductDetailsUtils.hasProductExtraProps(currentProduct)) {
            dispatch(ProductActions.restoreProductWithNoExtraDetails());
        }

        const productId = currentProduct?.productDetails?.productId;
        const skuId = currentProduct?.currentSku?.skuId;
        const isRecognized = userUtils.isRecognized();
        const zipCode = userUtils.getZipCode();
        const promise = isRecognized && zipCode ? userLocationUtils.updatePreferredZipCode({ postalCode: zipCode }) : Promise.resolve();

        promise.then(() => {
            profileApi
                .getSameDaySpecificProductDetails(userProfileId, productId, skuId, 'pdp')
                .then(data => {
                    dispatch(ProductActions.updateSameDayDeliveryProductDetails(data));
                    handleSameDayResponse(data);
                })
                .catch(handleSameDayError);
        });
    };

    const selectSameDay = (sdAvailable, isUpdatedZipCode) => {
        if (selectedMethod === 'sameday' && !isUpdatedZipCode) {
            return;
        }

        setSelectedMethod('sameday');
        setProductExtraDetailsLoaded(false);
        setSameDayAvailable(sdAvailable);
        setServiceUnavailable(false);
        setSameDayNotAvailableForZip(false);
        fetchSameDayDetails();
    };

    useEffect(() => {
        const isKohls = storeUtils.isKohlsStore(preferredStoreInfo);
        setIsKohlsStore(isKohls);
    }, [preferredStoreInfo]);

    useEffect(() => {
        if (
            userUtils.isAnonymous() &&
            stateRef.current.selectedMethod === 'pickup' &&
            !stateRef.current.productExtraDetailsLoaded &&
            preferredStoreInfo?.storeId
        ) {
            selectPickupRef.current(undefined, true);
        }
    }, [preferredStoreInfo]);

    useEffect(() => {
        if (prevZipCodeRef.current !== preferredZipCode) {
            if (stateRef.current.selectedMethod === 'sameday' && preferredZipCode) {
                selectSameDay(true, true);
            }

            if (preferredZipCode) {
                dispatch(getFulfillmentOptions());
            }
        }

        prevZipCodeRef.current = preferredZipCode;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preferredZipCode]);

    useEffect(() => {
        const newProductId = currentProduct?.productDetails?.productId;

        if (prevProductIdRef.current && newProductId && prevProductIdRef.current !== newProductId) {
            if (!stateRef.current.isBopisFirstOption) {
                if (currentProduct?.noExtraPropertiesProduct) {
                    dispatch(ProductActions.restoreProductWithNoExtraDetails());
                }

                setSelectedMethod('shipped');
                setProductExtraDetailsLoaded(true);
            }

            // GUAR-8371: ProductActions.openPage already dispatches
            // getFulfillmentOptions for framework-initiated SPA nav. Only
            // dispatch here for eligible SKUs (STANDARD, in-stock) and when
            // the response is not already in the store — mirrors the gate
            // at openPage / ProductPage.componentDidMount so OOS and non-
            // STANDARD SKUs don't reintroduce the unnecessary SDN call.
            if (isPdpFulfillmentEligibleSku(currentProduct?.currentSku) && !currentProduct?.fulfillmentOptions) {
                dispatch(getFulfillmentOptions());
            }
        }

        prevProductIdRef.current = newProductId;
    }, [currentProduct?.productDetails?.productId, dispatch]);

    return {
        selectedMethod,
        productExtraDetailsLoaded,
        sameDayAvailable,
        serviceUnavailable,
        sameDayNotAvailableForZip,
        isSDUAddedToBasket,
        skuTrialEligibility,
        skuTrialPeriod,
        isUserSDUTrialEligible,
        isSDUBannerVisible,
        trackedStoreId,
        isKohlsStore,
        isBopisFirstOption,
        selectShipped,
        selectPickup,
        selectSameDay,
        resetGetItShipped,
        setTrackedStoreId
    };
};

export default useFulfillment;
